import {
    Condition,
    ConditionType,
    PredefinedFilter
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { formatDateToDateString, IDateInterval, isSameMonth } from "@components/inputs/date/utils";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import { ISelectionWithEditableAmountCustomTableData } from "@components/smart/selectionTableView/SelectionTableView.utils";
import { TFormTable } from "@components/smart/smartFormGroup/SmartFormGroup";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { fetchReportTableData, IFetchReportTableDataArgs } from "@components/smart/smartTable/SmartReportTable.utils";
import { ICellValueObject } from "@components/table";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import {
    CompanyEntity,
    CompanyVatReducedCoefficientEntity,
    DocumentEntity,
    EntitySetName,
    EntityTypeName,
    ICompanyEntity,
    ICompanyVatReducedCoefficientEntity,
    IDocumentEntity,
    IElectronicSubmissionEntity,
    IInvoiceReceivedEntity,
    IOtherLiabilityEntity,
    IVatStatementDocumentEntity,
    VatStatementDocumentEntity
} from "@odata/GeneratedEntityTypes";
import {
    ClearedStatusCode,
    DocumentTypeCode,
    ElectronicSubmissionTypeCode,
    PostedStatusCode,
    VatReverseChargeCode,
    VatStatementFrequencyCode,
    VatStatementStatusCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { isBatchResultOk, OData } from "@odata/OData";
import { IFormatOptions, transformToODataString } from "@odata/OData.utils";
import {
    getVatCancelReapplyDateForPeriod,
    VatCancellationEntities,
    VatCancelReapplyProps
} from "@pages/admin/vatRules/VatRules.utils";
import { VatStatementPeriod } from "@pages/companies/Company.types";
import { DOCUMENT_JOURNAL_PATH, DocumentJournalProps } from "@pages/reports/documentJournal/DocumentJournalDef";
import { VAT_CONTROL_STATEMENT_REPORT_PATH } from "@pages/reports/vatControlStatementReport/VatControlStatementReportDef";
import { OSS_STATEMENT_REPORT_PATH } from "@pages/reports/vatOssStatementReport/VatOssStatementReportDef";
import { VatReportProps } from "@pages/reports/VatReport.utils";
import { VAT_STATEMENT_REPORT_PATH } from "@pages/reports/vatStatementReport/VatStatementReportDef";
import { VAT_VIES_STATEMENT_REPORT_PATH } from "@pages/reports/vatVIESStatementReport/VatVIESStatementReportDef";
import { isAccountAssignmentCompanyValue, isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import { TFetchFn } from "@utils/oneFetch";
import { Dayjs } from "dayjs";
import i18next from "i18next";

import { DASH_CHARACTER } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { LogicOperator, ReportTableRowType, Sort, ValueType } from "../../enums";
import { TValue } from "../../global.types";
import BindingContext from "../../odata/BindingContext";
import {
    ROUTE_DOCUMENT_JOURNAL,
    ROUTE_VAT_CONTROL_STATEMENT_REPORT,
    ROUTE_VAT_OSS_STATEMENT_REPORT,
    ROUTE_VAT_STATEMENT_REPORT,
    ROUTE_VAT_VIES_STATEMENT_REPORT
} from "../../routes";
import DateType, { DATE_MAX, DATE_MIN, DateFormat, getUtcDate, getUtcDayjs } from "../../types/Date";
import { CommonReportProps, getStatusFilterId } from "../reports/CommonDefs";
import { IReportData, ReportColumnType, ReportFilterNodeColumnType, ReportNodeOperator } from "../reports/Report.utils";

export interface IVatSubmissionCountArgs {
    submission?: IElectronicSubmissionEntity;
    from?: Date;
    to?: Date;
    drillDown?: boolean;
}

interface IGetDocumentJournalFilterDef {
    id: string;
    value: TValue;
    type: ReportColumnType;
}

export const PREVIOUS_SUBMISSIONS_TABLE_ID = "ElectronicSubmissionsTabsTable";

export function getDocumentJournalStatusFilter(statusType: EntityTypeName.VatStatementStatus | EntityTypeName.ClearedStatus | EntityTypeName.PostedStatus, values: (ClearedStatusCode | VatStatementStatusCode | PostedStatusCode)[]): IGetDocumentJournalFilterDef {
    return {
        id: BindingContext.localContext("DocumentStatus"),
        value: [{
            type: ConditionType.Included,
            filter: PredefinedFilter.Value,
            condition: Condition.Equals,
            value: values.map(code => getStatusFilterId(statusType, code))
        }],
        type: ReportColumnType.String
    };
}

export type TVatSubmissionDocumentsData = Partial<Record<ElectronicSubmissionTypeCode, number>>;

export type TVatCancelReapplyDocumentsData = Partial<{
    toBeCancelled: Partial<IInvoiceReceivedEntity | IOtherLiabilityEntity>[]
    Cancelled: Partial<IInvoiceReceivedEntity | IOtherLiabilityEntity>[]
    toBeReapplied: Partial<IInvoiceReceivedEntity | IOtherLiabilityEntity>[];
    Reapplied: Partial<IInvoiceReceivedEntity | IOtherLiabilityEntity>[];
}>;

interface IGetVatSubmissionReportArgs {
    type: ElectronicSubmissionTypeCode;
    isLocked: boolean;
    isCorrective?: boolean;
    isDrilldown?: boolean;
}

export const getVatSubmissionReportArgs = ({
                                               type,
                                               isLocked,
                                               isCorrective,
                                               isDrilldown
                                           }: IGetVatSubmissionReportArgs) => {
    let path: string;
    const vatStatementStatus: string[] = [];

    vatStatementStatus.push(isLocked ? VatStatementStatusCode.Filed : VatStatementStatusCode.NotFiled);

    if (isLocked || isCorrective) {
        vatStatementStatus.push(VatStatementStatusCode.FiledAndModified);
    }

    if (!isLocked) {
        vatStatementStatus.push(VatStatementStatusCode.Cancelled);
        vatStatementStatus.push(VatStatementStatusCode.Reapplied);
    }

    switch (type) {
        case ElectronicSubmissionTypeCode.VatStatement:
            path = isDrilldown ? ROUTE_VAT_STATEMENT_REPORT : VAT_STATEMENT_REPORT_PATH;
            break;
        case ElectronicSubmissionTypeCode.VatControlStatement:
            path = isDrilldown ? ROUTE_VAT_CONTROL_STATEMENT_REPORT : VAT_CONTROL_STATEMENT_REPORT_PATH;
            break;
        case ElectronicSubmissionTypeCode.VatVIESStatement:
            path = isDrilldown ? ROUTE_VAT_VIES_STATEMENT_REPORT : VAT_VIES_STATEMENT_REPORT_PATH;
            break;
        case ElectronicSubmissionTypeCode.VatOSSStatement:
            path = isDrilldown ? ROUTE_VAT_OSS_STATEMENT_REPORT : OSS_STATEMENT_REPORT_PATH;
    }

    return {
        path, value: vatStatementStatus
    };
};

export const getDocumentJournalReportArgs = ({
                                                 type,
                                                 isLocked,
                                                 isCorrective,
                                                 isDrilldown
                                             }: IGetVatSubmissionReportArgs) => {
    const vatStatementStatus: string[] = [];

    vatStatementStatus.push(isLocked ? VatStatementStatusCode.Filed : VatStatementStatusCode.NotFiled);

    if (isLocked || isCorrective) {
        vatStatementStatus.push(VatStatementStatusCode.FiledAndModified);
    }

    if (!isLocked) {
        vatStatementStatus.push(VatStatementStatusCode.Cancelled);
        vatStatementStatus.push(VatStatementStatusCode.Reapplied);
    }

    let columnName: string;

    switch (type) {
        case ElectronicSubmissionTypeCode.VatStatement:
            columnName = "VatStatementStatus_Name";
            break;
        case ElectronicSubmissionTypeCode.VatControlStatement:
            columnName = "VatControlStatementStatus_Name";
            break;
        case ElectronicSubmissionTypeCode.VatVIESStatement:
            columnName = "VatViesStatementStatus_Name";
            break;
    }

    return {
        path: isDrilldown ? ROUTE_DOCUMENT_JOURNAL : DOCUMENT_JOURNAL_PATH,
        value: vatStatementStatus,
        columnName
    };
};

export interface IGetVatSubmissionDocumentsDataArgs {
    subArgs: IVatSubmissionCountArgs;
    type?: ElectronicSubmissionTypeCode;
    isLocked?: boolean;
    isCorrective?: boolean;
    fetchFn?: TFetchFn;
}

export async function getVatSubmissionDocumentsData(args: IGetVatSubmissionDocumentsDataArgs): Promise<TVatSubmissionDocumentsData> {
    const { type, isLocked, isCorrective, subArgs } = args;
    const { path, value } = getVatSubmissionReportArgs({
        type, isLocked, isCorrective
    });
    const fetchArgs: IFetchReportTableDataArgs = {
        path,
        settings: {
            [BindingContext.cleanLocalContext(VatReportProps.vatStatementPeriod)]: {
                DateStart: subArgs.submission?.DatePeriodStart ?? subArgs?.from,
                DateEnd: subArgs.submission?.DatePeriodEnd ?? subArgs?.to
            }
        },
        reportHierarchy: {
            Aggregate: false,
            Columns: [
                { ColumnAlias: "Document_NumberOurs" }
            ],
            Groups: [],
            Aggregations: []
        }
    };

    if (type !== ElectronicSubmissionTypeCode.VatOSSStatement) {
        fetchArgs.settings[VatReportProps.vatStatementStatus] = value;
    }

    const response = await fetchReportTableData(fetchArgs, args.fetchFn);


    const retVal: TVatSubmissionDocumentsData = {};

    if (response?.ok) {
        const data = (await response.json()) as IReportData;
        const uniqueIds = new Set<string>();

        (data.Rows ?? []).forEach((row) => {
            if (row.Type === ReportTableRowType.Value) {
                // one document can be in multiple rows, so we need to only count unique ids
                uniqueIds.add(row.Value.Document_NumberOurs as string);
            }
        });

        retVal[type] = uniqueIds.size;
    }

    return retVal;
}

export type TCancelOrReapply = "Cancelled" | "Reapplied";

export interface ICancelReapplyTypeArgs {
    isProcessed?: boolean;
    period?: IDateInterval;
    type?: TCancelOrReapply;
}

export function getCancelReapplyTranslation(key: string, args: ICancelReapplyTypeArgs): string {
    const { isProcessed, type } = args;
    const prefix = type === "Cancelled" ? "Cancel" : "Reapply";
    const processed = isProcessed ? "Processed" : "";
    return i18next.t(`ElectronicSubmission:CancelReapplyDialog.${prefix}${key}${processed}`);
}

export function getCancelReapplyFilters(type: TCancelOrReapply, isProcessed: boolean, period: IDateInterval): string[] {
    const { from: periodStartDate, to: periodEndDate } = period;

    if (isProcessed) {
        const prop = type === "Cancelled" ? VatStatementDocumentEntity.DateVatCancellation : VatStatementDocumentEntity.DateVatReapplication;
        return [
            `${prop} ge ${formatDateToDateString(periodStartDate)}`,
            `${prop} le ${formatDateToDateString(periodEndDate)}`
        ];
    }

    const filters: string[] = [
        `${VatStatementDocumentEntity.DocumentTypeCode} in (${transformToODataString(VatCancellationEntities, ValueType.String)})`
    ];

    switch (type) {
        case "Cancelled":
            const sixMonthAgo = getUtcDayjs(periodEndDate).startOf("month").subtract(6, "month").endOf("month").toDate();

            filters.push(
                    `not(${VatStatementDocumentEntity.VatStatementStatusCode} in (${transformToODataString([VatStatementStatusCode.N_A, VatStatementStatusCode.NotFiled, VatStatementStatusCode.Undefined], ValueType.String)}))`,
                    `(${VatStatementDocumentEntity.ClearedStatusCode} ne ${transformToODataString(ClearedStatusCode.Cleared, ValueType.String)} or ${VatStatementDocumentEntity.DateClearing} gt ${formatDateToDateString(periodEndDate)})`,
                    `${VatStatementDocumentEntity.DateIssued} ge ${formatDateToDateString("2025-01-01")}`,
                    `${VatStatementDocumentEntity.DateDue} le ${formatDateToDateString(sixMonthAgo)}`,
                    `${VatStatementDocumentEntity.TransactionAmountVat} gt 0`,
                    `${VatStatementDocumentEntity.TransactionAmountDue} ge 0`,
                    `${VatStatementDocumentEntity.DateVatCancellation} eq null`,
                    `(${VatStatementDocumentEntity.VatReverseChargeCode} eq ${transformToODataString(VatReverseChargeCode.Ne, ValueType.String)}
                    or ${VatStatementDocumentEntity.VatReverseChargeCode} eq null)`,
                    `not(${VatStatementDocumentEntity.IsInAutomatedVatInclusionMode})`,
            );
            break;
        case "Reapplied":

            filters.push(
                    `${VatStatementDocumentEntity.DateVatCancellation} lt ${formatDateToDateString(periodStartDate)}`,
                    `${VatStatementDocumentEntity.ClearedStatusCode} eq '${ClearedStatusCode.Cleared}'`,
                    `${VatStatementDocumentEntity.DateClearing} le ${formatDateToDateString(periodEndDate)}`,
                    `${VatStatementDocumentEntity.DateVatReapplication} eq null`
            );
            break;
    }

    return filters;
}

export async function getCancelReapplyDocuments(oData: OData, args: IGetVatSubmissionDocumentsDataArgs): Promise<TVatCancelReapplyDocumentsData> {
    const { subArgs } = args;
    const from = subArgs.submission?.DatePeriodStart ?? subArgs?.from;
    const to = subArgs.submission?.DatePeriodEnd ?? subArgs?.to;
    const period = { from, to };

    const _fetchData = async (type: TCancelOrReapply, isProcessed = false): Promise<(IInvoiceReceivedEntity | IOtherLiabilityEntity)[]> => {
        const query = oData.getEntitySetWrapper(EntitySetName.VatStatementDocuments)
                .query()
                .select(VatStatementDocumentEntity.Id, VatStatementDocumentEntity.NumberOurs, VatStatementDocumentEntity.NumberTheirs,
                        VatStatementDocumentEntity.DateDue, VatStatementDocumentEntity.TransactionAmountVat,
                        VatStatementDocumentEntity.TransactionCurrencyCode, VatStatementDocumentEntity.DocumentTypeCode,
                        VatStatementDocumentEntity.DateVatCancellation, VatStatementDocumentEntity.DateVatReapplication)
                .filter(getCancelReapplyFilters(type, isProcessed, period).join(" and "));

        const result = await query.fetchData();

        return result.value ?? [];
    };

    const [toBeCancelledDocs, cancelledDocs, toBeReappliedDocs, reappliedDocs] = await Promise.all([
        _fetchData("Cancelled"),
        _fetchData("Cancelled", true),
        _fetchData("Reapplied"),
        _fetchData("Reapplied", true),
    ]);

    return {
        toBeCancelled: toBeCancelledDocs,
        Cancelled: cancelledDocs,
        toBeReapplied: toBeReappliedDocs,
        Reapplied: reappliedDocs
    };
}

export interface ICancelReapplyArgs {
    period: VatStatementPeriod;
    changedDocuments: IVatStatementDocumentEntity[];
    type: TCancelOrReapply;
    isReversal?: boolean;
}

export async function applyCancelOrReapplyDateOnDocuments(oData: OData, args: ICancelReapplyArgs, storage: TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>): Promise<string | true> {
    const error = i18next.t("ElectronicSubmission:VatSubmission.ErrorUpdatingDocuments");

    const { period, changedDocuments, type, isReversal } = args;
    const isCancel = type === "Cancelled";
    const propName = isCancel ? VatCancelReapplyProps.DateVatCancellation : VatCancelReapplyProps.DateVatReapplication;
    const { pairedItems } = storage.getCustomData();

    try {
        const batch = oData.batch();
        batch.beginAtomicityGroup("update-documents");

        changedDocuments.forEach(doc => {
            let formattedDate: string;
            let Amount: number = null;
            if (isReversal) {
                formattedDate = null;
            } else {
                const date = getVatCancelReapplyDateForPeriod(doc, period);
                formattedDate = transformToODataString(date, ValueType.Date);
                if (isCancel) {
                    const rowId = storage.data.bindingContext.addKey(doc.Id).toString();
                    Amount = pairedItems[rowId].TransactionAmount;
                }
            }
            const updatedDoc: IInvoiceReceivedEntity | IOtherLiabilityEntity = {
                [propName]: formattedDate,
                ...(isCancel ? { VatCancellationAmount: Amount } : {})
            };
            const entitySet = doc.DocumentTypeCode === DocumentTypeCode.InvoiceReceived
                    ? EntitySetName.InvoicesReceived : EntitySetName.OtherLiabilities;
            batch.getEntitySetWrapper(entitySet).update(doc.Id, updatedDoc);
        });

        const results = await batch.execute();

        if (!results.every(res => isBatchResultOk(res))) {
            return error;
        }
    } catch(e) {
        return error;
    }

    return true;
}

export async function getDocumentJournalDocumentsData(args: IGetVatSubmissionDocumentsDataArgs): Promise<TVatSubmissionDocumentsData> {
    const { type, isLocked, isCorrective, subArgs } = args;
    const { path, value, columnName } = getDocumentJournalReportArgs({
        type, isLocked, isCorrective
    });
    const dateStart = subArgs.submission?.DatePeriodStart ?? subArgs?.from;
    const dateEnd = subArgs.submission?.DatePeriodEnd ?? subArgs?.to;

    const fetchArgs: IFetchReportTableDataArgs = {
        path,
        settings: {
            [BindingContext.cleanLocalContext(CommonReportProps.dateRange)]: {
                DateStart: formatDateToDateString(DATE_MIN),
                DateEnd: formatDateToDateString(DATE_MAX)
            },
            [BindingContext.cleanLocalContext(DocumentJournalProps.dateClearingStatusTo)]: formatDateToDateString(getUtcDate()),
            Filter: {
                "Type": ReportFilterNodeColumnType.Logic,
                "Operator": LogicOperator.And,
                "Left": {
                    "Type": ReportFilterNodeColumnType.Logic,
                    "Operator": LogicOperator.And,
                    "Left": {
                        "Type": ReportFilterNodeColumnType.Date,
                        "Operator": ReportNodeOperator.GreaterOrEqual,
                        "Left": {
                            "ColumnAlias": "Document_DateDocumentVatStatement"
                        },
                        "Right": {
                            "Value": formatDateToDateString(getUtcDayjs(dateStart))
                        }
                    },
                    "Right": {
                        "Type": ReportFilterNodeColumnType.Date,
                        "Operator": ReportNodeOperator.LessOrEqual,
                        "Left": {
                            "ColumnAlias": "Document_DateDocumentVatStatement"
                        },
                        "Right": {
                            "Value": formatDateToDateString(getUtcDayjs(dateEnd))
                        }
                    }
                },
                "Right": {
                    "Type": ReportFilterNodeColumnType.Logic,
                    "Operator": LogicOperator.Or,
                    "Left": {
                        "Left": {
                            "ColumnAlias": columnName
                        },
                        "Right": {
                            // todo use enum id instead of label once its ready on backend
                            "Value": getEnumDisplayValue(EntityTypeName.VatStatementStatus, VatStatementStatusCode.FiledAndModified)
                        },
                        "Type": ReportFilterNodeColumnType.String,
                        "Operator": ReportNodeOperator.Equal
                    },
                    "Right": {
                        "Left": {
                            "ColumnAlias": columnName
                        },
                        "Right": {
                            // todo use enum id instead of label once its ready on backend
                            "Value": getEnumDisplayValue(EntityTypeName.VatStatementStatus, VatStatementStatusCode.NotFiled)
                        },
                        "Type": ReportFilterNodeColumnType.String,
                        "Operator": ReportNodeOperator.Equal
                    }
                }
            }
        },
        reportHierarchy: {
            Aggregate: false,
            Columns: [
                { ColumnAlias: "Document_NumberOurs" },
                { ColumnAlias: "Document_DateDocumentVatStatement" },
                { ColumnAlias: columnName }
            ],
            Groups: [],
            Aggregations: []
        }
    };


    const response = await fetchReportTableData(fetchArgs, args.fetchFn);


    const retVal: TVatSubmissionDocumentsData = {};

    if (response?.ok) {
        const data = (await response.json()) as IReportData;
        const uniqueIds = new Set<string>();

        (data.Rows ?? []).forEach((row) => {
            if (row.Type === ReportTableRowType.Value) {
                // one document can be in multiple rows, so we need to only count unique ids
                uniqueIds.add(row.Value.Document_NumberOurs as string);
            }
        });

        retVal[type] = uniqueIds.size;
    }

    return retVal;
}

export async function getVatSubmissionClearingRatio(oData: OData, context: IAppContext, year: number): Promise<ICompanyVatReducedCoefficientEntity> {
    const companyId = context.getCompanyId();
    const query = oData.getEntitySetWrapper(EntitySetName.Companies)
        .query(companyId)
        .select(CompanyEntity.UseVatReducedDeduction, CompanyEntity.Id)
        .expand(CompanyEntity.VatReducedCoefficients, q =>
            q.filter(`${CompanyVatReducedCoefficientEntity.Year} eq ${year}`).select(CompanyVatReducedCoefficientEntity.VatReducedCoefficient, CompanyVatReducedCoefficientEntity.Id));

    const result = await query.fetchData<ICompanyEntity>();
    const company = result.value;

    if (company.UseVatReducedDeduction) {
        return company.VatReducedCoefficients?.[0] ?? {
            Year: year,
            VatReducedCoefficient: 0
        };
    }
    return null;
}

export async function changeVatSubmissionClearingRatio(oData: OData, context: IAppContext, coeff: ICompanyVatReducedCoefficientEntity): Promise<void> {
    const companyId = context.getCompanyId();

    await oData.getEntitySetWrapper(EntitySetName.Companies).update(companyId, {
        [`${CompanyEntity.VatReducedCoefficients}@odata.delta`]: [coeff]
    });
}

export const getLiabilityDocumentTableDef = (context: IAppContext): TFormTable => {
    return {
        id: `LiabilityDocumentTable`,
        entitySet: EntitySetName.Documents,
        initialSortBy: [
            { id: "DateAccountingTransaction", sort: Sort.Asc }
        ],
        columns: [
            {
                id: DocumentEntity.DateAccountingTransaction,
                label: i18next.t("ElectronicSubmission:Tax.Date"),
                isVisible: isAccountAssignmentCompanyValue
            },
            {
                id: DocumentEntity.DateCbaDocument,
                label: i18next.t("ElectronicSubmission:Tax.Date"),
                isVisible: (args: IGetValueArgs) => isCashBasisAccountingCompany(args.context)
            },
            {

                id: DocumentEntity.DocumentType,
                fieldSettings: {
                    displayName: "Name"
                },
                label: i18next.t("ElectronicSubmission:Tax.DocumentType")
            },
            {
                id: DocumentEntity.NumberOurs,
                label: i18next.t("ElectronicSubmission:Tax.Document"),
                formatter: (val: TValue, args?: IFormatOptions<IDocumentEntity, IDocumentEntity>) => {
                    const { item } = args;
                    const { Id, NumberOurs } = item ?? {};

                    return Id ? getTableIntentLink(NumberOurs, {
                        route: `${getRouteByDocumentType(item.DocumentTypeCode as DocumentTypeCode)}/${Id}`,
                        context
                    }) : null;
                }
            },
            {
                id: DocumentEntity.Note,
                label: i18next.t("ElectronicSubmission:Tax.Note")
            },
            {
                id: DocumentEntity.Amount,
                label: i18next.t("ElectronicSubmission:Tax.Amount")
            }
        ]
    };
};

function isVatStatementPeriod(value: any): value is VatStatementPeriod {
    return value?.from && value?.to;
}

export function getVatSubmissionFrequencyFromPeriod(period: IElectronicSubmissionEntity | VatStatementPeriod): VatStatementFrequencyCode {
    let from: Date, to: Date;
    if (isVatStatementPeriod(period)) {
        from = period.from?.toDate();
        to = period.to?.toDate();
    } else {
        from = period.DatePeriodStart;
        to = period.DatePeriodEnd;
    }
    return isSameMonth(from, to) ? VatStatementFrequencyCode.Monthly : VatStatementFrequencyCode.Quarterly;
}

export function getVatSubmissionPeriodSortValue(date: Date | Dayjs, frequency: VatStatementFrequencyCode): TValue {
    if (!frequency || !date) {
        return DATE_MIN;
    }
    return getUtcDayjs(date).startOf(frequency === VatStatementFrequencyCode.Monthly ? "month" : "quarter").toDate();
}

export function getVatSubmissionPeriodTableCell(date: Date | Dayjs, frequency: VatStatementFrequencyCode, numeric?: boolean): ICellValueObject {
    if (!frequency || !date) {
        return { value: DASH_CHARACTER, tooltip: DASH_CHARACTER };
    }
    let value: string;
    const periodDate = getUtcDayjs(date);
    switch (frequency) {
        case VatStatementFrequencyCode.Quarterly:
            const quarter = periodDate.quarter();
            const year = periodDate.get("year");
            value = numeric ? `${periodDate.get("year")}/Q${periodDate.quarter()}`
                : i18next.t("ElectronicSubmission:VatSubmission.Quarter", { count: quarter, year, ordinal: true });
            break;
        case VatStatementFrequencyCode.Monthly:
            value = numeric ? periodDate.format("M/YY") : DateType.format(periodDate, DateFormat.monthAndYear);
            break;
    }
    return { value, tooltip: value };
}

export function getVatSubmissionPeriodName(date: Date | Dayjs, frequency: VatStatementFrequencyCode, numeric?: boolean): string {
    return getVatSubmissionPeriodTableCell(date, frequency, numeric).value as string;
}

export function isSubmissionLastPeriod(vatFrequency: VatStatementFrequencyCode, monthIndex: number): boolean {
    return vatFrequency === VatStatementFrequencyCode.Monthly ? monthIndex === 11 : monthIndex === 9;
}

export const isOssDeadlineMissed = (date: Date | Dayjs): boolean => {
    // deadline is last day of month after the end of current quarter
    const endOfQuarter = getUtcDayjs(date).endOf("quarter");
    const deadline = endOfQuarter.add(1, "month").endOf("month");
    const today = getUtcDayjs();

    return today.isAfter(deadline, "date");
};